import { Badge, DialogTitle } from "@mui/material";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { GridColDef } from "@mui/x-data-grid";
import { DataGridPremium } from "@mui/x-data-grid-premium";
import { ProjectTimeEntryHistory } from "@repo/server-api";
import dayjs from "dayjs";
import { useTranslation } from "next-i18next";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";

export default function DialogHistoryTimeEntry(
	props: Readonly<{
		open: boolean;
		setOpen: (value: boolean) => void;
		history: ProjectTimeEntryHistory[];
	}>,
) {
	const { t, i18n } = useTranslation();
	const [history, setHistory] = useState<ProjectTimeEntryHistory[]>([]);

	const [open, setOpen] = useState(false);

	useEffect(() => {
		setOpen(props.open);
	}, [props.open]);

	const handleClose = () => {
		setOpen(false);
		props.setOpen(false);
	};

	useEffect(() => {
		setHistory(props.history.map((entry) => ({ id: uuidv4(), ...entry })));
	}, [props.history]);

	const columns: GridColDef<ProjectTimeEntryHistory>[] = [
		{
			field: "status",
			headerName: t("status"),
			renderCell: (params) => {
				let fontColor = "#000";
				let bgColor = "#f9a825";
				switch (params.row.status) {
					case "APPROVED":
						fontColor = "#fff";
						bgColor = "#4caf50";
						break;
					case "REJECTED":
						fontColor = "#000";
						bgColor = "#f44336";
						break;
					case "PENDING":
						fontColor = "#000";
						bgColor = "#f9a825";
						break;
					case "OPEN":
						fontColor = "#000";
						bgColor = "lightblue";
						break;
					default:
						fontColor = "#000";
						bgColor = "#f9a825";
						break;
				}
				return (
					<Badge
						style={{
							justifyContent: "center",
							alignItems: "center",
							display: "flex",
							backgroundColor: bgColor,
							color: fontColor,
							borderRadius: "10px",
							padding: "5px 10px",
							fontSize: "12px",
							fontWeight: "bold",
							width: "70%",
							height: "20px",
							margin: "7px auto",
						}}>
						{t("time-entry.status." + params.row.status.toLowerCase(), {
							ns: "enum",
						})}
					</Badge>
				);
			},
		},
		{
			field: "startEnd",
			headerName: t("start-till-end"),
			width: 120,
			renderCell: (params) => {
				const start = dayjs(params.row.start).utc().format("HH:mm");
				const end = dayjs(params.row.end).utc().format("HH:mm");
				const text = `${start} → ${end}`;
				return <div style={{ marginTop: "15px" }}>{text}</div>;
			},
		},
		{
			field: "workingHours",
			headerName: t("working-hours"),
			align: "center",
			width: 120,
			headerAlign: "center",
			renderCell: (params) => {
				const start = dayjs(params.row.start);
				const end = dayjs(params.row.end);
				const hours = Math.floor(end.diff(start, "hour"));
				const minutes = end.diff(start, "minute") % 60;

				return (
					<div
						style={{
							justifyContent: "center",
							alignItems: "center",
							display: "flex",
							marginTop: "15px",
						}}>
						{
							<div>
								{hours > 0 ? `${hours}h` : ""} {minutes > 0 ? `${minutes}m` : ""}
							</div>
						}
					</div>
				);
			},
		},
		{
			field: "project",
			headerName: t("project-name"),
			width: 170,
			renderCell: (params) => {
				return (
					<div
						style={{
							color: "#000",
							fontSize: "12px",
							fontWeight: "bold",
							marginTop: "15px",
						}}>
						{params.row.projectPosition.projectName}
					</div>
				);
			},
		},
		{
			field: "projectPosition",
			headerName: t("project-position"),
			width: 130,
			renderCell: (params) => {
				return (
					<div
						style={{
							color: "#000",
							fontSize: "12px",
							fontWeight: "bold",
							marginTop: "15px",
						}}>
						{params.row.projectPosition.saleableServiceName}
					</div>
				);
			},
		},
		{
			field: "description",
			headerName: t("description"),
			flex: 1,
			renderCell: (params) => {
				return (
					<div
						style={{
							color: "#000",
							fontSize: "12px",
							fontWeight: "bold",
							whiteSpace: "pre-wrap",
							wordWrap: "break-word",
							marginTop: "15px",
						}}
						title={params.row.description || t("no-description")}>
						{params.row.description || t("no-description")}
					</div>
				);
			},
		},
		{
			field: "createdBy",
			headerName: t("modified-by"),
			width: 150,
			renderCell: (params) => {
				return <div style={{ marginTop: "15px" }}>{params.row.createdBy.displayName}</div>;
			},
		},
		{
			field: "timestamp",
			headerName: t("modified-at"),
			width: 200,
			renderCell: (params) => {
				return (
					<div style={{ marginTop: "15px" }}>
						{i18n.language === "de"
							? dayjs(params.row.timestamp).utc().local().format("DD.MM.YYYY HH:mm")
							: dayjs(params.row.timestamp).utc().local().format("MM/DD/YYYY hh:mm A")}
					</div>
				);
			},
		},
	];

	return (
		<Dialog open={open} aria-describedby="alert-dialog-description" maxWidth="lg" fullWidth>
			<DialogTitle>{t("change-log")}</DialogTitle>
			<DialogContent>
				<DataGridPremium
					getRowId={(row) => row.id}
					getRowHeight={() => "auto"}
					columns={columns}
					rows={history}
					autoHeight
					disableRowSelectionOnClick
					hideFooter
				/>
			</DialogContent>
			<DialogActions>
				<Button onClick={handleClose}>{t("ok")}</Button>
			</DialogActions>
		</Dialog>
	);
}
